import React, { ChangeEvent, Dispatch, Fragment, SetStateAction, useEffect, useRef, useState } from 'react';
import moment from 'moment';

import { ArrowDown, ArrowUp, Minus, Search, User } from 'lucide-react';

import { TextInput } from '../../../shared/Inputs';
import { ExclusiveMultiFilterSelect } from '../../../shared/ExclusiveMultiFilterSelect';

import { useGetDashboardMBTSAnalyses } from '../../../../hooks/dashboard';
import { useGetSubClients } from '../../../../hooks/sub_clients';
import { useGetCohorts } from '../../../../hooks/cohorts';

import { IMBTSAnalysisListItem, TMBTSAnalysisTrendDirection } from '../../../../types/services.interfaces/dashboard';

const formatScore = (score: number, scoreMax: number) => `${score.toFixed(1)}/${scoreMax}`;

export const AnalysesList = () => {
    const [filteredAnalyses, setFilteredAnalyses] = useState<IMBTSAnalysisListItem[]>([]);

    const { data: analyses, isLoading } = useGetDashboardMBTSAnalyses();

    return (
        <div className="dashboard__mbts__analyses_list">
            <Filters
                analyses={analyses}
                setFilteredAnalyses={setFilteredAnalyses}
            />
            {isLoading && (
                <div className="dashboard__mbts__analyses_list__loading">
                    <div className='list-loader' />
                    <p className="text-medium">Loading Reports...</p>
                </div>
            )}
            {!isLoading && (
                <Fragment>
                    {!analyses?.length && (
                        <div className="dashboard__mbts__analyses_list__empty">
                            <p className="text-medium">No reports yet!</p>
                        </div>
                    )}
                    {!!analyses?.length && (
                        <Fragment>
                            {!filteredAnalyses.length && (
                                <div className="dashboard__mbts__analyses_list__empty">
                                    <p className="text-medium">No reports found.</p>
                                </div>
                            )}
                            {!!filteredAnalyses.length && (
                                <div className="dashboard__mbts__analyses_list__table_wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Employee</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Email</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Sub Client</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Cohorts</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Scenario</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Score</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Created At</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Action</p></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAnalyses.map((analysis) => (
                                                <AnalysisRow key={analysis.analysis_id} analysis={analysis} />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Fragment>
                    )}
                </Fragment>
            )}
        </div>
    );
};

interface IFiltersProps {
    analyses: IMBTSAnalysisListItem[] | undefined;
    setFilteredAnalyses: Dispatch<SetStateAction<IMBTSAnalysisListItem[]>>;
};
const Filters = (props: IFiltersProps) => {
    const { analyses, setFilteredAnalyses } = props;

    const hasInitializedSubClientFilter = useRef(false);
    const hasInitializedCohortFilter = useRef(false);

    const [filterInputValue, setFilterInputValue] = useState<string>('');
    const [isSubClientFilterReady, setIsSubClientFilterReady] = useState<boolean>(false);
    const [subClientFilter, setSubClientFilter] = useState<{ includeNone: boolean; selectedIds: string[] }>({ includeNone: true, selectedIds: [] });
    const [isCohortFilterReady, setIsCohortFilterReady] = useState<boolean>(false);
    const [cohortFilter, setCohortFilter] = useState<{ includeNone: boolean; selectedIds: string[] }>({ includeNone: true, selectedIds: [] });

    const { data: subClients } = useGetSubClients(true);
    const { data: cohorts } = useGetCohorts(true);

    useEffect(() => {
        if (!subClients || hasInitializedSubClientFilter.current) return;

        hasInitializedSubClientFilter.current = true;
        setSubClientFilter({
            includeNone: true,
            selectedIds: subClients.map(subClient => subClient.id.toString()),
        });
        setIsSubClientFilterReady(true);
    }, [subClients]);

    useEffect(() => {
        if (!cohorts || hasInitializedCohortFilter.current) return;

        hasInitializedCohortFilter.current = true;
        setCohortFilter({
            includeNone: true,
            selectedIds: cohorts.map(cohort => cohort.id.toString()),
        });
        setIsCohortFilterReady(true);
    }, [cohorts]);

    useEffect(() => {
        if (!analyses) return;

        const searchValue = filterInputValue.toLowerCase();
        const isAllSubClientsSelected = !!subClients?.length
            && subClients.every(subClient => subClientFilter.selectedIds.includes(subClient.id.toString()));
        const isAllCohortsSelected = !!cohorts?.length
            && cohorts.every(cohort => cohortFilter.selectedIds.includes(cohort.id.toString()));

        setFilteredAnalyses(analyses.filter(analysis => {
            const matchesSearch = !searchValue
                || analysis.first_name?.toLowerCase().includes(searchValue)
                || analysis.last_name?.toLowerCase().includes(searchValue)
                || analysis.email?.toLowerCase().includes(searchValue)
                || analysis.sub_client?.name?.toLowerCase().includes(searchValue)
                || analysis.cohorts.some(cohort => cohort.name?.toLowerCase().includes(searchValue))
                || analysis.scenario_title?.toLowerCase().includes(searchValue);

            const matchesSubClient = !isSubClientFilterReady
                ? true
                : subClientFilter.includeNone && (isAllSubClientsSelected || !subClients?.length)
                    ? true
                    : subClientFilter.includeNone
                        ? !analysis.sub_client
                        : subClientFilter.selectedIds.includes(analysis.sub_client?.id.toString() ?? '');

            const matchesCohort = !isCohortFilterReady
                ? true
                : cohortFilter.includeNone && (isAllCohortsSelected || !cohorts?.length)
                    ? true
                    : cohortFilter.includeNone
                        ? !analysis.cohorts.length
                        : analysis.cohorts.some(cohort => cohortFilter.selectedIds.includes(cohort.id.toString()));

            return matchesSearch && matchesSubClient && matchesCohort;
        }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyses, filterInputValue, isSubClientFilterReady, subClientFilter, isCohortFilterReady, cohortFilter, subClients, cohorts]);

    const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
        setFilterInputValue(e.target.value);
    };

    return (
        <div className="dashboard__mbts__analyses_list__filters">
            <TextInput
                icon={{ icon: <Search />, position: 'prefix' }}
                placeholder="Search participants"
                value={filterInputValue}
                onChange={handleFilter}
                disabled={!analyses?.length}
            />
            <ExclusiveMultiFilterSelect
                noneLabel="No Sub Client"
                groupAllLabel="All Sub Clients"
                items={(subClients ?? []).map(subClient => ({ id: subClient.id.toString(), name: subClient.name }))}
                includeNone={subClientFilter.includeNone}
                selectedIds={subClientFilter.selectedIds}
                disabled={!analyses?.length}
                onChange={setSubClientFilter}
            />
            <ExclusiveMultiFilterSelect
                noneLabel="No Cohort"
                groupAllLabel="All Cohorts"
                items={(cohorts ?? []).map(cohort => ({ id: cohort.id.toString(), name: cohort.name }))}
                includeNone={cohortFilter.includeNone}
                selectedIds={cohortFilter.selectedIds}
                disabled={!analyses?.length}
                onChange={setCohortFilter}
            />
        </div>
    );
};

const AnalysisRow = ({ analysis }: { analysis: IMBTSAnalysisListItem }) => {
    const getTrendIcon = (direction: TMBTSAnalysisTrendDirection) => {
        if (direction === 'up') return <ArrowUp size={14} />;
        if (direction === 'down') return <ArrowDown size={14} />;
        return <Minus size={14} />;
    };

    const initials = `${analysis.first_name?.charAt(0)?.toUpperCase() ?? ''}${analysis.last_name?.charAt(0)?.toUpperCase() ?? ''}`;

    return (
        <tr>
            <td>
                <div className="dashboard__mbts__analyses_list__table__employee_cell">
                    <div>
                        {(analysis.first_name || analysis.last_name)
                            ? <p>{initials}</p>
                            : <User />
                        }
                    </div>
                    <p className="text-sm text-medium">{analysis.first_name} {analysis.last_name}</p>
                </div>
            </td>
            <td><p className="text-sm color--txt-muted">{analysis.email ?? '-'}</p></td>
            <td><p className="text-sm color--txt-muted">{analysis.sub_client?.name ?? '-'}</p></td>
            <td>
                <p className="text-sm color--txt-muted">
                    {analysis.cohorts.length
                        ? analysis.cohorts.map((cohort, index) => `${cohort.name}${index < analysis.cohorts.length - 1 ? ', ' : ''}`)
                        : '-'}
                </p>
            </td>
            <td>
                <p className="text-sm color--txt-muted">{analysis.scenario_title || '-'}</p>
            </td>
            <td>
                <div className="dashboard__mbts__analyses_list__table__score_cell">
                    <p className="text-sm color--txt-muted">{formatScore(analysis.score, analysis.score_max)}</p>
                    <span
                        className={[
                            'dashboard__mbts__analyses_list__table__trend_pill',
                            `dashboard__mbts__analyses_list__table__trend_pill--${analysis.trend.direction}`,
                        ].join(' ')}
                    >
                        {getTrendIcon(analysis.trend.direction)}
                        <p className="text-xs text-medium">{analysis.trend.percentage}%</p>
                    </span>
                </div>
            </td>
            <td><p className="text-sm color--txt-muted">{moment(analysis.created_at).format('MMM DD, YYYY')}</p></td>
            <td>
                <div className="dashboard__mbts__analyses_list__table__actions_cell">
                    <button className="link link--primary text-sm text-semibold">View Report</button>
                </div>
            </td>
        </tr>
    );
};
